/**
 * ApplicationPackagePanel — first-class top-of-workspace component that
 * surfaces the uploaded application package (Rental App PDF + supporting
 * docs) as NTN's source of truth for every verification workspace.
 *
 * Design:
 *   - Rental Application PDF pinned as a primary card. Click → slide-over
 *     viewer (iframe for PDF / <img> for images) with an "Open in new tab"
 *     fallback.
 *   - Supporting documents rendered as a clickable list with type icons.
 *   - The verifier never re-enters applicant data — this panel is the
 *     surface they consume the source of truth from.
 */
import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { fmtDate } from "@/lib/dateFormat";
import {
  Package, FileText, Image as ImageIcon, ExternalLink, Eye, ChevronDown, ChevronUp,
  FileCheck2, Paperclip, Download,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function docHref(id) { return `${BACKEND}/api/files/${id}/download`; }
function isPdf(d) { return (d?.content_type || "").includes("pdf") || /\.pdf$/i.test(d?.original_filename || ""); }
function isImage(d) { return (d?.content_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(d?.original_filename || ""); }

function LabelChip({ label }) {
  const l = (label || "document").replace(/_/g, " ");
  return <Badge variant="outline" className="text-[10px] capitalize">{l}</Badge>;
}

function DocIcon({ doc, className = "h-4 w-4" }) {
  if (isImage(doc)) return <ImageIcon className={className} />;
  return <FileText className={className} />;
}

/** Right-side slide-over that previews a single document. */
function DocumentViewer({ doc, open, onOpenChange }) {
  if (!doc) return null;
  const href = docHref(doc.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0 gap-0" data-testid="doc-viewer-dialog">
        <DialogHeader className="p-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <DocIcon doc={doc} className="h-5 w-5 text-slate-500" />
            <DialogTitle className="font-serif text-lg truncate flex-1 min-w-0">
              {doc.original_filename || "Document"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Preview of the uploaded application package file. Use Open in new tab or Download to view outside the workspace.
            </DialogDescription>
            <LabelChip label={doc.label} />
            {doc.original_size && <span className="text-[10px] font-mono text-slate-500 uppercase">{fmtSize(doc.original_size)}</span>}
            <a href={href} target="_blank" rel="noreferrer"
               className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 border border-slate-200 rounded-md px-2 py-1"
               data-testid="doc-viewer-open-new-tab">
              <ExternalLink className="h-3 w-3" /> Open in new tab
            </a>
            <a href={href} download={doc.original_filename}
               className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 border border-slate-200 rounded-md px-2 py-1"
               data-testid="doc-viewer-download">
              <Download className="h-3 w-3" /> Download
            </a>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-slate-100 overflow-hidden" data-testid="doc-viewer-body">
          {isPdf(doc) && (
            <iframe title={doc.original_filename || "PDF"} src={href} className="w-full h-full border-0" data-testid="doc-viewer-iframe" />
          )}
          {isImage(doc) && (
            <div className="w-full h-full overflow-auto flex items-start justify-center p-4">
              <img src={href} alt={doc.original_filename || ""} className="max-w-full h-auto" data-testid="doc-viewer-image" />
            </div>
          )}
          {!isPdf(doc) && !isImage(doc) && (
            <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 gap-3 p-6 text-center">
              <FileText className="h-12 w-12 text-slate-300" />
              <div className="font-medium text-slate-700">Preview not available for this file type</div>
              <div className="text-xs">{doc.content_type || "unknown"} · {fmtSize(doc.original_size)}</div>
              <a href={href} target="_blank" rel="noreferrer"
                 className="mt-2 inline-flex items-center gap-1 border border-slate-300 rounded-md px-3 py-2 text-slate-700 hover:bg-white">
                <ExternalLink className="h-4 w-4" /> Open file
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ApplicationPackagePanel({ documents }) {
  const [viewer, setViewer] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const { rentalApp, supporting } = useMemo(() => {
    const list = Array.isArray(documents) ? documents.slice() : [];
    // Most-recent first.
    list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    const rentalApp = list.find((d) => d.label === "rental_application") || null;
    const supporting = list.filter((d) => d !== rentalApp);
    return { rentalApp, supporting };
  }, [documents]);

  const hasAnything = rentalApp || supporting.length > 0;

  return (
    <Card className="p-4 bg-[#F3EFEA]/40 border-[#0A192F]/10" data-testid="application-package-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-sm bg-[#0A192F]/6 flex items-center justify-center">
            <Package className="h-4 w-4 text-[#0A192F]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="ntn-eyebrow text-slate-600">Application package</div>
            <div className="text-[11px] text-slate-500">
              {hasAnything
                ? `${(rentalApp ? 1 : 0) + supporting.length} file${(rentalApp ? 1 : 0) + supporting.length === 1 ? "" : "s"} · source of truth for this verification`
                : "No files uploaded yet — awaiting submission."}
            </div>
          </div>
        </div>
        {hasAnything && (
          <Button variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}
                  className="rounded-md h-8" data-testid="package-toggle">
            {expanded ? <><ChevronUp className="h-3.5 w-3.5 mr-1" /> Collapse</> : <><ChevronDown className="h-3.5 w-3.5 mr-1" /> Expand</>}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="space-y-3">
          {/* Pinned rental application */}
          {rentalApp && (
            <button
              type="button"
              onClick={() => setViewer(rentalApp)}
              className="w-full text-left bg-white border border-[#0A192F]/15 rounded-sm p-3 hover:border-[#0A192F]/40 hover:shadow-sm transition group"
              data-testid="rental-application-card"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-[#0A192F] text-white flex items-center justify-center shrink-0">
                  <FileCheck2 className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="ntn-eyebrow text-[#F59E0B] mb-0.5">Rental application · primary</div>
                  <div className="font-medium text-slate-900 truncate">{rentalApp.original_filename || "Rental Application"}</div>
                  <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">
                    {(rentalApp.content_type || "file").split("/").pop()} · {fmtSize(rentalApp.original_size)}
                    {rentalApp.created_at && ` · ${fmtDate(rentalApp.created_at)}`}
                  </div>
                </div>
                <span className="text-xs text-slate-500 group-hover:text-slate-900 flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" /> Open
                </span>
              </div>
            </button>
          )}

          {/* Supporting documents */}
          {supporting.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Supporting documents ({supporting.length})
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {supporting.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setViewer(d)}
                    className="text-left bg-white border border-[#0A192F]/12 rounded-sm p-2.5 hover:border-[#0A192F]/35 hover:shadow-sm transition group flex items-center gap-2.5"
                    data-testid={`support-doc-${d.id}`}
                  >
                    <DocIcon doc={d} className="h-4 w-4 text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">{d.original_filename || "Document"}</div>
                      <div className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">
                        <LabelChip label={d.label} />
                        <span className="ml-1">{fmtSize(d.original_size)}</span>
                      </div>
                    </div>
                    <Eye className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-700 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {!hasAnything && (
            <div className="text-xs text-slate-500 italic border border-dashed border-slate-300 rounded-sm p-4 text-center">
              No files uploaded for this applicant yet. Once the PM submits the package, it will appear here.
            </div>
          )}
        </div>
      )}

      <DocumentViewer doc={viewer} open={!!viewer} onOpenChange={(o) => !o && setViewer(null)} />
    </Card>
  );
}
